import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTE } from '@/constants';
import { UserContainer } from '@/providers';
import { usePlanLimits } from '@/hooks/use-plan-limits';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import teamLogo from '@/views/settings/team/images/team-icon.svg';
import professionalLogo from '@/views/settings/team/images/professional-icon.svg';
import { useSession } from '@/states/common';
import { useMemo } from 'react';
import { IPlan } from '@/components/subscription-change-modal/constant';
import { usePlanRecommendation } from '@/hooks/use-plan-recommendation';

interface LimitEnforcementModalProps {
  open: boolean;
  currentPlan: string;
}

export const LimitEnforcementModal = ({
  open,
  currentPlan,
}: LimitEnforcementModalProps) => {
  const { t } = useTranslation(['pricing', 'billing']);
  const navigate = useNavigate();
  const { data: session } = useSession();
  const paymentPlans = session?.paymentPlans;
  const selectedClient = session.client;

  const {
    suggestedPlan,
    planConfig,
    exceededCount,
    primaryResource,
    handleUpgrade,
    handleContactSupport,
    handleManageResources,
    limits,
    current,
    exceeded,
    isOverLimit,
    teamPlan,
    professionalPlan,
  } = usePlanRecommendation();

  const datasourceCurrent = current.datasources;
  const datasourceLimit =
    limits.datasources === 'unlimited' ? 'unlimited' : limits.datasources;

  const workspaceCurrent = current.workspaces;
  const workspaceLimit = limits.workspaces;

  const userCurrent = current.users;
  const userLimit =
    typeof limits.users === 'number' ? limits.users : 'unlimited';

  // Generate hero title
  const getHeroTitle = () => {
    if (suggestedPlan === 'contact_support') {
      return t(
        'billing:contact_support_title',
        'Contact Support for Additional Limits'
      );
    }

    if (exceededCount > 1) {
      return t('billing:upgrade_to_continue', 'Upgrade to Continue');
    }

    switch (primaryResource) {
      case 'workspaces':
        return t('billing:unlock_workspaces', 'Unlock More Workspaces');
      case 'datasources':
        return t('billing:unlock_data_sources', 'Unlock More Data Sources');
      case 'users':
        return t('billing:unlock_team_members', 'Unlock More Team Members');
      default:
        return t('billing:upgrade_to_scale', 'Upgrade to Keep Scaling');
    }
  };

  // Generate subtitle
  const getSubtitle = () => {
    if (suggestedPlan === 'contact_support') {
      return t(
        'billing:contact_support_message',
        'Your usage has exceeded our Team plan limits. Please contact our support team to discuss additional limits that fit your needs.'
      );
    }

    if (exceededCount === 1) {
      switch (primaryResource) {
        case 'workspaces': {
          return t(
            'billing:workspace_limit_message',
            `Your plan includes ${workspaceLimit} workspaces, but you're currently using ${workspaceCurrent}.`,
            {
              count: Number(workspaceLimit),
              limit: workspaceLimit,
              current: workspaceCurrent,
            }
          );
        }
        case 'datasources': {
          return t(
            'billing:datasource_limit_message',
            `Your plan includes ${datasourceLimit} data sources, but you're currently using ${datasourceCurrent}.`,
            {
              count: Number(datasourceLimit),
              limit: datasourceLimit,
              current: datasourceCurrent,
            }
          );
        }
        case 'users': {
          return t(
            'billing:user_limit_message',
            `Your plan includes ${userLimit} users, but you're currently using ${userCurrent}.`,
            {
              count: Number(userLimit),
              limit: userLimit,
              current: userCurrent,
            }
          );
        }
        default:
          return t(
            'billing:upgrade_required',
            'Please upgrade to continue using all features.'
          );
      }
    }

    const exceededCategories = [];
    if (exceeded.workspaces) exceededCategories.push('workspaces');
    if (exceeded.datasources) exceededCategories.push('data sources');
    if (exceeded.users) exceededCategories.push('users');

    return t(
      'billing:multiple_limits_message',
      `You've exceeded your plan limits for ${exceededCategories.join(', ')}.`,
      {
        categories: exceededCategories.join(', '),
      }
    );
  };

  // Show "Contact Support" UI if exceeds Team plan
  if (suggestedPlan === 'contact_support') {
    return (
      <Dialog open={open}>
        <DialogContent
          className="max-w-2xl p-0 gap-0 max-h-[90vh] overflow-y-auto"
          hideDefaultCloseButton
          onPointerDownOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => e.preventDefault()}
        >
          <div className="p-6 text-center border-b">
            <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 rounded-full flex items-center justify-center">
              <i className="ti ti-rocket text-primary text-3xl" />
            </div>
            <DialogTitle className="text-2xl font-semibold text-gray-900 mb-2">
              {getHeroTitle()}
            </DialogTitle>
            <DialogDescription className="text-base text-gray-600">
              {getSubtitle()}
            </DialogDescription>
          </div>

          <div className="px-8 py-6 border-b bg-gray-50">
            <div className="text-xs font-medium text-gray-500 uppercase mb-4">
              {t('billing:your_current_usage', 'Your Current Usage')}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div
                className={cn(
                  'bg-white p-4 rounded-lg border text-center',
                  exceeded.datasources
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200'
                )}
              >
                <i
                  className={cn(
                    'ti ti-database text-2xl mb-2',
                    exceeded.datasources ? 'text-blue-600' : 'text-gray-400'
                  )}
                />
                <div
                  className={cn(
                    'text-2xl font-bold',
                    exceeded.datasources ? 'text-blue-900' : 'text-gray-900'
                  )}
                >
                  {datasourceCurrent}
                  {teamPlan?.requirements.datasources !== 'unlimited' && (
                    <span className="text-sm text-gray-500">
                      /{teamPlan?.requirements.datasources}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-600">
                  {t('billing:data_sources', 'Data Sources')}
                </div>
              </div>
              <div
                className={cn(
                  'bg-white p-4 rounded-lg border text-center',
                  exceeded.workspaces
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200'
                )}
              >
                <i
                  className={cn(
                    'ti ti-layout-grid text-2xl mb-2',
                    exceeded.workspaces ? 'text-blue-600' : 'text-gray-400'
                  )}
                />
                <div
                  className={cn(
                    'text-2xl font-bold',
                    exceeded.workspaces ? 'text-blue-900' : 'text-gray-900'
                  )}
                >
                  {workspaceCurrent}
                  {teamPlan?.requirements.workspace !== 'unlimited' && (
                    <span className="text-sm text-gray-500">
                      /{teamPlan?.requirements.workspace}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-600">
                  {t('billing:workspaces', 'Workspaces')}
                </div>
              </div>
              <div
                className={cn(
                  'bg-white p-4 rounded-lg border text-center',
                  exceeded.users
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200'
                )}
              >
                <i
                  className={cn(
                    'ti ti-users text-2xl mb-2',
                    exceeded.users ? 'text-blue-600' : 'text-gray-400'
                  )}
                />
                <div
                  className={cn(
                    'text-2xl font-bold',
                    exceeded.users ? 'text-blue-900' : 'text-gray-900'
                  )}
                >
                  {userCurrent}
                  {teamPlan?.requirements.user !== 'unlimited' && (
                    <span className="text-sm text-gray-500">
                      /{teamPlan?.requirements.user}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-600">
                  {t('billing:users', 'Users')}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 p-4 border-t bg-gray-50 justify-end">
            <Button
              intent="secondary"
              onClick={handleManageResources}
              startIcon={<i className="ti ti-settings" />}
            >
              {t('billing:manage_resources', 'Manage resources')}
            </Button>
            <Button
              onClick={handleContactSupport}
              startIcon={<i className="ti ti-message-circle" />}
            >
              {t('billing:contact_support', 'Contact Support')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Don't show modal if plan data isn't loaded
  if (!planConfig) {
    return null;
  }

  const { requirements } = planConfig;

  // Regular upgrade UI
  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 max-h-[90vh] overflow-y-auto"
        hideDefaultCloseButton
        onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
      >
        <div className="p-6 text-center border-b">
          <img
            src={suggestedPlan === 'team' ? teamLogo : professionalLogo}
            alt=""
            className="w-16 h-16 mx-auto"
          />
          <DialogTitle className="text-2xl font-semibold text-gray-900 mb-2">
            {getHeroTitle()}
          </DialogTitle>
          <DialogDescription className="text-base text-gray-600">
            {getSubtitle()}
          </DialogDescription>
        </div>

        <div className="px-8 py-4">
          <div className="text-center mb-4">
            <p className="text-sm text-gray-900 font-medium">
              {t(
                'billing:upgrade_comparison_headline',
                'Upgrade to continue without interruption'
              )}
            </p>
          </div>
          <div className="grid grid-cols-[1fr_auto_1fr] gap-4 mx-auto items-start">
            <div className="flex flex-col bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-xs font-medium text-gray-500 uppercase mb-3">
                {t('billing:current_usage', 'Current Usage')}
              </div>
              <div className="space-y-2.5">
                <div
                  className={cn(
                    'flex items-start gap-2 text-sm',
                    exceeded.datasources
                      ? 'text-gray-900 font-semibold'
                      : 'text-gray-600'
                  )}
                >
                  <i className="ti ti-database text-base mt-0.5" />
                  <span>
                    {datasourceLimit === 'unlimited'
                      ? t(
                          'billing:datasource_usage_unlimited',
                          '{{current}} data sources',
                          { current: datasourceCurrent }
                        )
                      : t(
                          'billing:datasource_usage_with_limit',
                          '{{current}}/{{limit}} data sources',
                          { current: datasourceCurrent, limit: datasourceLimit }
                        )}
                  </span>
                </div>
                <div
                  className={cn(
                    'flex items-start gap-2 text-sm',
                    exceeded.workspaces
                      ? 'text-gray-900 font-semibold'
                      : 'text-gray-600'
                  )}
                >
                  <i className="ti ti-layout-grid text-base mt-0.5" />
                  <span>
                    {workspaceLimit === 'unlimited'
                      ? t(
                          'billing:workspace_usage_unlimited',
                          '{{current}} workspaces',
                          { current: workspaceCurrent }
                        )
                      : t(
                          'billing:workspace_usage_with_limit',
                          '{{current}}/{{limit}} workspaces',
                          {
                            current: workspaceCurrent,
                            limit: workspaceLimit,
                          }
                        )}
                  </span>
                </div>
                <div
                  className={cn(
                    'flex items-start gap-2 text-sm',
                    exceeded.users
                      ? 'text-gray-900 font-semibold'
                      : 'text-gray-600'
                  )}
                >
                  <i className="ti ti-users text-base mt-0.5" />
                  <span>
                    {userLimit === 'unlimited'
                      ? t('billing:user_usage_unlimited', '{{current}} users', {
                          current: userCurrent,
                        })
                      : t(
                          'billing:user_usage_with_limit',
                          '{{current}}/{{limit}} users',
                          {
                            current: userCurrent,
                            limit: userLimit,
                          }
                        )}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center h-full">
              <i className="ti ti-arrow-right text-2xl text-gray-400" />
            </div>

            <div className="flex flex-col bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="text-xs font-medium text-gray-900 uppercase mb-3">
                {planConfig.title} {t('billing:recommended', '(Recommended)')}
              </div>
              <div className="space-y-2.5">
                <div
                  className={cn(
                    'flex items-start gap-2 text-sm',
                    exceeded.datasources
                      ? 'text-gray-900 font-bold'
                      : 'text-gray-900 font-medium'
                  )}
                >
                  <i className="ti ti-database text-base mt-0.5" />
                  <span>
                    {requirements.datasources === 'unlimited'
                      ? t(
                          'billing:data-sources_unlimited',
                          'Unlimited data sources'
                        )
                      : t(
                          'billing:data-sources_other',
                          '{{count}} data sources',
                          {
                            count: requirements.datasources,
                          }
                        )}
                  </span>
                </div>
                <div
                  className={cn(
                    'flex items-start gap-2 text-sm',
                    exceeded.workspaces
                      ? 'text-gray-900 font-bold'
                      : 'text-gray-900 font-medium'
                  )}
                >
                  <i className="ti ti-layout-grid text-base mt-0.5" />
                  <span>
                    {requirements.workspace === 'unlimited'
                      ? t('billing:workspace_unlimited', 'Unlimited workspaces')
                      : t('billing:workspace_other', '{{count}} workspaces', {
                          count: requirements.workspace,
                        })}
                  </span>
                </div>
                <div
                  className={cn(
                    'flex items-start gap-2 text-sm',
                    exceeded.users
                      ? 'text-gray-900 font-bold'
                      : 'text-gray-900 font-medium'
                  )}
                >
                  <i className="ti ti-users text-base mt-0.5" />
                  <span>
                    {requirements.user === 'unlimited'
                      ? t('billing:users_unlimited', 'Unlimited users')
                      : t('billing:users_other', '{{count}} users', {
                          count: requirements.user,
                        })}
                  </span>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-900 font-medium">
                  <i className="ti ti-check text-green-600 text-base mt-0.5" />
                  <span>{t('billing:room_to_grow', 'Room to grow')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-4 border-t bg-gray-50 justify-end">
          <Button
            intent="secondary"
            onClick={handleManageResources}
            startIcon={<i className="ti ti-settings" />}
          >
            {t('billing:manage_resources', 'Manage resources')}
          </Button>
          <Button
            onClick={handleUpgrade}
            startIcon={<i className="ti ti-bolt" />}
          >
            {t('billing:upgrade_to_plan', `Upgrade to ${planConfig.title}`, {
              planTitle: planConfig.title,
            })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
